"use strict";
// Connected Accounts: the ways this account can be signed into.
//
// Every row comes from the authMethods GET /me already returns — type,
// masked identifier, trust level, when it was linked, whether it was
// revoked. Nothing here calls anything new and nothing is invented; this
// screen existed as data long before it existed as a screen.
//
// There is no "disconnect" control. Revoking an auth method is a real
// operation with a real hazard — removing the last one would lock somebody
// out of their own account — and RevokeAuthMethodUseCase has no HTTP
// surface. Adding a button that cannot work, or one that can lock a person
// out, are both worse than showing the truth and saying what is coming.
(function () {
    function t(hubEl, key, fallback) {
        return hubEl.dataset[key] || fallback;
    }
    var esc = function (value) {
        return window.FlixHubFormat.escapeHtml(value);
    };
    var LINK_ICON = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7"/><path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7"/></svg>';
    var GOOGLE_MARK = '<svg viewBox="0 0 48 48" width="19" height="19" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.5-4.6 2.4-7.2 2.4-5.2 0-9.6-3.3-11.2-7.9l-6.5 5C9.6 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C41.4 36 44 30.5 44 24c0-1.3-.1-2.3-.4-3.5z"/></svg>';
    var DISCORD_MARK = '<svg viewBox="0 0 24 24" width="19" height="19" aria-hidden="true"><path fill="#fff" d="M20.32 4.57A19.8 19.8 0 0 0 15.42 3c-.24.43-.5 1-.68 1.46a18.3 18.3 0 0 0-5.49 0A12 12 0 0 0 8.56 3a19.7 19.7 0 0 0-4.9 1.57C.55 9.2-.3 13.7.13 18.15a19.9 19.9 0 0 0 6.03 3.05c.49-.66.92-1.36 1.29-2.1-.71-.27-1.39-.6-2.03-.99.17-.12.34-.25.5-.38a14.2 14.2 0 0 0 12.15 0c.17.14.34.26.5.38-.64.39-1.32.72-2.03.99.37.74.8 1.44 1.29 2.1a19.8 19.8 0 0 0 6.03-3.05c.5-5.16-.85-9.62-3.56-13.58zM8.02 15.42c-1.18 0-2.15-1.08-2.15-2.4 0-1.33.95-2.41 2.15-2.41 1.2 0 2.17 1.09 2.15 2.4 0 1.33-.95 2.41-2.15 2.41zm7.96 0c-1.18 0-2.15-1.08-2.15-2.4 0-1.33.95-2.41 2.15-2.41 1.2 0 2.17 1.09 2.15 2.4 0 1.33-.94 2.41-2.15 2.41z"/></svg>';
    var MAIL_MARK = '<svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/></svg>';
    var CHROME = {
        google: { mark: GOOGLE_MARK, background: "#ffffff", color: "#ffffff" },
        discord: { mark: DISCORD_MARK, background: "#5865f2", color: "#ffffff" },
        email: { mark: MAIL_MARK, background: "var(--surface2)", color: "var(--accent)" },
    };
    function sectionLabelHtml(label) {
        return ('<div style="font-family:var(--mono);font-size:10px;letter-spacing:2px;color:var(--accent);margin:0 0 10px;text-transform:uppercase">' +
            esc(label) +
            "</div>");
    }
    /** The provider's own name where we have one, never a raw type string. */
    function providerLabel(hubEl, type) {
        var key = "i18nProvider" + type.charAt(0).toUpperCase() + type.slice(1);
        var named = hubEl.dataset[key];
        if (named)
            return named;
        if (type === "email")
            return t(hubEl, "i18nSettingsEmail", "Email");
        return type;
    }
    /**
     * What to show beside the provider.
     *
     * An email address is the person's own and is masked, as everywhere else
     * in the Hub. A provider's subject identifier is a meaningless number to
     * the person it belongs to, so the link date is shown instead — that is
     * the fact they can actually recognise.
     */
    function identifierLine(hubEl, method, locale) {
        if (method.type === "email") {
            return window.FlixHubFormat.maskEmail(method.externalIdentifier);
        }
        var template = t(hubEl, "i18nConnectedSince", "Connected %%date%%");
        return template.replace("%%date%%", window.FlixHubFormat.date(method.linkedAt, locale));
    }
    /**
     * The backend's own rule, mirrored: a method can be signed in with
     * unless it has been revoked or was never proven.
     *
     * Mirrored, not relocated — the Account aggregate is still the authority
     * and still refuses. This only decides whether to OFFER the button,
     * because offering a Remove that is guaranteed to fail is worse than
     * not offering one.
     */
    function isEligibleForSignIn(method) {
        return method.revokedAt === null && method.trustLevel !== "unverified";
    }
    /**
     * Removing a way into your own account deserves a second press, so the
     * button arms first. Two presses, in place, rather than a dialog: the
     * dialog in this Hub is reserved for signing out, which ends the session
     * you are using, and borrowing it here would overstate this.
     */
    function removeButtonHtml(hubEl, method) {
        return ('<button type="button" data-flix-connected-remove="' +
            esc(method.id) +
            '" data-armed="false" data-label-remove="' +
            esc(t(hubEl, "i18nConnectedRemove", "Remove")) +
            '" data-label-confirm="' +
            esc(t(hubEl, "i18nConnectedRemoveConfirm", "Tap again to remove")) +
            '" style="flex:none;background:none;border:1px solid var(--border);border-radius:10px;' +
            "padding:7px 13px;color:var(--error);font-family:var(--body);font-size:13px;font-weight:700;" +
            'cursor:pointer;transition:border-color .2s" style-hover="border-color:var(--error)">' +
            esc(t(hubEl, "i18nConnectedRemove", "Remove")) +
            "</button>");
    }
    function methodRowHtml(hubEl, method, locale, removable) {
        var _a;
        var chrome = (_a = CHROME[method.type]) !== null && _a !== void 0 ? _a : {
            mark: LINK_ICON,
            background: "var(--surface2)",
            color: "var(--dim)",
        };
        var verified = method.trustLevel === "verified"
            ? '<span style="display:inline-flex;align-items:center;flex:none;padding:3px 9px;border-radius:var(--radius-lg);' +
                "background:color-mix(in srgb, var(--success) 12%, transparent);border:1px solid color-mix(in srgb, var(--success) 38%, transparent);color:color-mix(in srgb, var(--success) 78%, #ffffff);" +
                'font-size:11px;font-weight:700">' +
                esc(t(hubEl, "i18nEmailVerified", "Verified")) +
                "</span>"
            : "";
        return ('<div data-flix-connected-method="' +
            esc(method.type) +
            '" style="display:flex;align-items:center;gap:13px;padding:14px 16px;border-bottom:1px solid var(--border)">' +
            '<span aria-hidden="true" style="width:38px;height:38px;flex:none;border-radius:11px;display:flex;' +
            "align-items:center;justify-content:center;background:" +
            chrome.background +
            ";color:" +
            chrome.color +
            '">' +
            chrome.mark +
            "</span>" +
            '<div style="flex:1;min-width:0">' +
            '<div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap">' +
            '<span style="font-size:14px;font-weight:700;color:var(--text)">' +
            esc(providerLabel(hubEl, method.type)) +
            "</span>" +
            verified +
            "</div>" +
            '<div style="font-size:12px;color:var(--dim);margin-top:2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">' +
            esc(identifierLine(hubEl, method, locale)) +
            "</div>" +
            "</div>" +
            (removable ? removeButtonHtml(hubEl, method) : "") +
            "</div>");
    }
    function render(hubEl, contentEl, profile) {
        var locale = document.documentElement.lang || "en";
        // A revoked method is no longer a way in, so it is not listed as one.
        var active = profile.authMethods.filter(function (method) {
            return method.revokedAt === null;
        });
        if (active.length === 0) {
            contentEl.innerHTML = window.FlixHubRender.emptyState({
                title: t(hubEl, "i18nConnectedEmptyTitle", "No connected accounts"),
                message: t(hubEl, "i18nConnectedEmptyMessage", "Sign-in methods you connect will be listed here."),
                iconSvg: LINK_ICON,
            });
            window.flixCompileHoverStyles(contentEl);
            return;
        }
        // With one way in left, there is nothing to offer: removing it would
        // lock this person out, and the backend would refuse anyway. The hint
        // below says so, so the missing button is explained rather than just
        // absent.
        var eligibleCount = active.filter(isEligibleForSignIn).length;
        contentEl.innerHTML =
            sectionLabelHtml(t(hubEl, "i18nConnectedLabel", "Sign-in methods")) +
                '<div data-flix-connected-list style="border-radius:var(--radius-md);background:var(--surface);' +
                'border:1px solid var(--border);overflow:hidden">' +
                active
                    .map((method) => methodRowHtml(hubEl, method, locale, eligibleCount > 1 || !isEligibleForSignIn(method)))
                    .join("") +
                "</div>" +
                '<div data-flix-connected-error hidden style="margin:12px 2px 0;padding:11px 14px;border-radius:12px;' +
                'background:rgba(224,86,74,.1);border:1px solid rgba(224,86,74,.4);color:var(--error);font-size:13px"></div>' +
                '<p style="font-size:12px;color:var(--faint);margin:12px 2px 0;line-height:1.6">' +
                esc(eligibleCount > 1
                    ? t(hubEl, "i18nConnectedHint", "Removing a sign-in method takes effect immediately. Adding one is coming soon.")
                    : t(hubEl, "i18nConnectedHintLast", "This is your only way to sign in, so it cannot be removed. Adding another is coming soon.")) +
                "</p>";
        var rows = contentEl.querySelectorAll("[data-flix-connected-method]");
        var last = rows[rows.length - 1];
        if (last)
            last.style.borderBottom = "none";
        window.flixCompileHoverStyles(contentEl);
    }
    function showError(contentEl, message) {
        var box = contentEl.querySelector("[data-flix-connected-error]");
        if (!box)
            return;
        box.textContent = message;
        box.hidden = false;
    }
    /**
     * Removes one sign-in method: arm on the first press, act on the second.
     *
     * Only the second press calls anything. On success the screen re-reads
     * the profile rather than editing the list in place — the server decides
     * what this account now has, and a removal changes what the OTHER rows
     * are allowed to offer.
     */
    async function remove(hubEl, contentEl, authMethodId) {
        var button = contentEl.querySelector('[data-flix-connected-remove="' + authMethodId.replace(/"/g, '\\"') + '"]');
        if (!button)
            return;
        if (button.dataset.armed !== "true") {
            button.dataset.armed = "true";
            button.textContent = button.dataset.labelConfirm || "Tap again to remove";
            button.style.borderColor = "var(--error)";
            return;
        }
        button.disabled = true;
        button.style.opacity = "0.6";
        try {
            await window.FlixAuthClient.revokeAuthMethod(authMethodId);
        }
        catch (err) {
            button.disabled = false;
            button.style.opacity = "";
            button.dataset.armed = "false";
            button.textContent = button.dataset.labelRemove || "Remove";
            button.style.borderColor = "";
            // 409 is the account's own invariant refusing, and it is the one
            // failure worth naming: the person needs to know they must add
            // another way in first, not that "something went wrong".
            var status = typeof err === "object" && err !== null && "status" in err
                ? err.status
                : 0;
            showError(contentEl, status === 409
                ? t(hubEl, "i18nConnectedLastError", "This is your only way to sign in, so it cannot be removed.")
                : t(hubEl, "i18nConnectedRemoveError", "We couldn't remove that. Please try again."));
            return;
        }
        var profile = await window.FlixAuthClient.getProfile();
        render(hubEl, contentEl, profile);
    }
    window.FlixHubConnected = { render: render, remove: remove };
})();
